// External
import { ActivatedRoute } from '@angular/router';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

// Internal
import { DevicesService } from "../../../services/process/devices/devices.service";
import { ParentalHelperService } from '../../../../pages/parental/parental.helper.service';
import { ParentalService } from '../../../services/process/parental/parental.service';
import { ParentalValuesService } from "../../../values/parental.values.service";
import { UsefulService } from "../../../services/global/useful/useful.service";
import { UtilsCommonService } from '../../../utils/utils-common.service';
import { OperatingSystems, ValuesService } from '../../../values/values.service';
import { LocalizedDatePipe } from 'src/app/common/pipes/pipes';

@Injectable({
    providedIn: 'root'
})

export class IParentalService {

    constructor(
        private readonly parentalService: ParentalService,
        private readonly translate: TranslateService,
        private readonly usefulService: UsefulService,
        private readonly parentalHelperService: ParentalHelperService,
        private readonly parentalValuesService: ParentalValuesService,
        private readonly valuesService: ValuesService,
        private readonly utilsCommonService: UtilsCommonService,
        private readonly devicesService: DevicesService,
        private readonly route: ActivatedRoute,
        private readonly localizedDatePipe: LocalizedDatePipe
    ) {
    }

    filters = {
        all: (device) => {
            if (device && device.device_os) {
                return true;
            } else {
                return false;
            }
        },
        ios: (device) => {
            if (device && device.device_os && device.device_os === OperatingSystems.IOS) {
                return true;
            } else {
                return false;
            }
        },
        iot: (device) => {
            if (device && device.device_os && !this.parentalValuesService.NON_IOT_PLATFORMS.has(device.device_os)) {
                return true;
            } else {
                return false;
            }
        },
        mobile: (device) => {
            if (device && device.device_os && this.parentalValuesService.MOBILE_PLATFORMS.has(device.device_os)) {
                return true;
            } else {
                return false;
            }
        },
        desktop: (device) => {
            if (device && device.device_os && this.parentalValuesService.DESKTOP_PLATFORMS.has(device.device_os)){
                return true;
            } else {
                return false;
            }
        },
        not_supported: (device) => {
            if (device && device.device_os && !this.parentalValuesService.SUPPORTED_OS.has(device.device_os)){
                return true;
            } else {
                return false;
            }
        },
        supported: (device) => {
            if (device && device.device_os && this.parentalValuesService.SUPPORTED_OS.has(device.device_os)) {
                return true;
            } else {
                return false;
            }
        },

    }

    filterProfileDevices(profileId) {
        const devices = this.devicesService.getParentalDevicesList();
        const profileDevices = [];
        for (const device of devices) {
            if (profileId === device.profile_id) {
                profileDevices.push(device);
            }
        }
        return profileDevices;
    }

    filterParentalDevices(profileHash) {
        const all = {};
        const ios = {};
        const iot = {};
        const mobile = {};
        const desktop = {};
        const notSupported = {};
        const supported = {};

        const profiles     = this.parentalService.get();
        const profileId    = this.usefulService.getNested(profiles, null, profileHash, "profile_id");
        const devices      = this.filterProfileDevices(profileId);
        const profile      = this.usefulService.getNested(profiles, null, profileHash);

        if (profile === null) {
            return;
        }
        
        profile.devices_monitored = {};
        for (const device of devices) {
            if (this.filters.all(device)) {
                all[device.device_id] = device;
                if (this.filters.mobile(device)) {
                    if (this.filters.ios(device)) {
                        ios[device.device_id] = device;
                        notSupported[device.device_id] = device;
                        mobile[device.device_id] = device;
                    } else {
                        mobile[device.device_id] = device;
                        supported[device.device_id] = device;
                    }
                } else if (this.filters.desktop(device)) {
                    supported[device.device_id] = device;
                    desktop[device.device_id] = device;
                } else {
                    iot[device.device_id] = device;
                    notSupported[device.device_id] = device;
                }
            }
        }
        profile.devices_monitored["all"] = all;
        profile.devices_monitored["devices_not_supported"] = notSupported;
        profile.devices_monitored["devices_supported"] = supported;
        profile.devices_monitored["ios"] = ios;
        profile.devices_monitored["iot"] = iot;
        profile.devices_monitored["mobile"] = mobile;
        profile.devices_monitored["desktop"] = desktop;
    }

    decidePauseState(profileHash) {
        // pause > dtl > schedule
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        let states = {};
        if (this.usefulService.getNested(profile, undefined, 'screentime', 'hourly', 'profile_status')) {
            const profileStatus = profile["screentime"]["hourly"]["profile_status"];
            if (profileStatus.pause.state) {
                states = {
                    pause: true,
                    dtl: false,
                    schedule: false
                };
            } else {
                if (profileStatus.blockDTL && profileStatus.blockDTL.state) {
                    const dtlDate = new Date(profileStatus.blockDTL.timestamp * 1000);
                    const todayDate = new Date();
                    if (dtlDate.getDate() === todayDate.getDate() &&
                        dtlDate.getMonth() === todayDate.getMonth() &&
                        dtlDate.getFullYear() === todayDate.getFullYear()) {
                            states = {
                                pause: false,
                                dtl: true,
                                schedule: false
                            };
                    }
                } else if (profileStatus.schedule_paused) {
                    states = {
                        pause: false,
                        dtl: false,
                        schedule: true
                    };
                } else {
                    states = {
                        pause: false,
                        dtl: false,
                        schedule: false
                    };
                }
            }
        }
        profile['interface']['cards']['today']["profile_status"] = this.usefulService.getNested(profile, {}, 'interface', 'cards', 'today', 'profile_status');
        profile['interface']['cards']['today']["profile_status"]["pause_states"] = states;
    }

    assignProfilesToDevices() {
        const devices = this.devicesService.getParentalDevices();
        const profiles = this.parentalService.get();
        for (const device of devices) {
            if (device.profile_id && device.profile_id !== 0) {
                for (const profileHash in profiles) {
                    if (device.profile_id === profiles[profileHash].profile_id) {
                        device.device_profile = profiles[profileHash];
                    }
                }
            }
        }
    }

    createDevicesInterface(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, null, profileHash);
        const screentimeDevices = profile['screentime_devices']['hourly'];
        let usedDevices       = 0;
        const devices           = this.devicesService.getParentalDevicesList();
        const devicesToDisplay  = [];
        const devicesSupported  =  this.usefulService.getNested(profile, {}, 'devices_monitored', 'devices_supported');

        const newScreentimeDevices = screentimeDevices.filter(
            x => {
                if (x.time_spent > 0) {
                    const timeAux = this.parentalHelperService.computeTime("ceil", x.time_spent);
                    x.hours = timeAux.hours;
                    x.minutes = timeAux.minutes;
                    usedDevices++;
                }
                return x.time_spent > 0;
            }
        );

        newScreentimeDevices.sort((device1, device2) => {
            return (device1.time_spent < device2.time_spent) ? 1 : -1;
        });

        for (const newScreentimeDevice of newScreentimeDevices) {
            for (const device of devices) {
                if ((newScreentimeDevice.device_id in devicesSupported)
                && newScreentimeDevice.device_id === device.device_id
                && device.profile_id !== 0 ) {
                    devicesToDisplay.push(newScreentimeDevice);
                }
            }
        }
        screentimeDevices['to_display'] = devicesToDisplay;
        profile["used_devices"] = usedDevices;

        this.addTypeOnDevice(profileHash);
    }

    addTypeOnDevice(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, null, profileHash);
        const devicesNotSupported =  this.usefulService.getNested(profile, {}, 'devices_monitored', 'devices_not_supported');
        if (Object.keys(devicesNotSupported).length > 0) {
            for (const deviceId of Object.keys(devicesNotSupported)) {
                const device =devicesNotSupported[deviceId];
                if (device.device_os && device.device_os === OperatingSystems.IOS) {
                    device.type = "iOS";
                } else {
                    device.type = "IoT";
                }
            }
        }
    }

    decideProfilePauseStatus(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const pauseStatus = this.parentalService.getProfileStatus("screentime", "hourly", profileHash);
        const timeInfo = this.parentalService.getHourlyTimeInfo("screentime", "hourly", profileHash);
        const pause = {
            text: '',
            state: false
        };

        if (pauseStatus && pauseStatus.pause && pauseStatus.pause.state) {
            pause.text = "parental.time.paused";
            pause.state = true;
        } else if (timeInfo.time_spent >= timeInfo.daily_limit && timeInfo.daily_limit > 0) {
            pause.text = "parental.time.limitReached";
            pause.state = true;
        } else if (pauseStatus && pauseStatus.schedule_paused) {
            pause.text = "parental.time.inSchedule";
            pause.state = true;
        }

        profile['interface']['cards']['today']["profile_status"] = this.usefulService.getNested(profile, {}, 'interface', 'cards', 'today', 'profile_status');
        profile['interface']['cards']['today']['profile_status']['state'] = pause.state;
        profile['interface']['cards']['today']['profile_status']['text'] = pause.text;
    }

    decideTextColor(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        this.decideProfilePauseStatus(profileHash);
        const timeInfo = this.parentalService.getHourlyTimeInfo("screentime", "hourly", profileHash);

        const text = {
            outOfLimits: false,
            exceedLimits: false
        };

        if (this.usefulService.getNested(profile, undefined, 'interface', 'cards', 'today', 'profile_status', 'state')) {
            text.outOfLimits = false;
            text.exceedLimits = true;
        } else if (timeInfo.time_spent_threshold < timeInfo.time_spent && timeInfo.daily_limit < 0) {
            text.outOfLimits = true;
            text.exceedLimits = false;
        } else {
            text.outOfLimits = false;
            text.exceedLimits = false;
        }

        profile['interface']['cards']['today']["profile_status"] = this.usefulService.getNested(profile, {}, 'interface', 'cards', 'today', 'profile_status');
        profile['interface']['cards']['today']["profile_status"]["outOfLimits"] = text.outOfLimits;
        profile['interface']['cards']['today']["profile_status"]["exceedLimits"] = text.exceedLimits;

    }

    decideFooterTodayCard(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const actionsInfo = this.parentalService.getHourlyActionsInfo("screentime", "hourly", profileHash);

        const footerState = {
            show: false,
            text: '',
            dtl: false,
            schedule: false
        };

        if (actionsInfo.message_id === 5) {
            footerState.show = false;
        } else {
            footerState.show = true;
            footerState.text = 'parental.screentime.text_' + actionsInfo.message_id;
            if (!actionsInfo.actions || Object.keys(actionsInfo.actions).length === 0) {
                footerState.dtl = false;
                footerState.schedule = false;
            } else if (Object.keys(actionsInfo.actions).length === 2) {
                footerState.dtl = true;
                footerState.schedule = true;
            } else if (Object.keys(actionsInfo.actions).length === 1) {
                if (Object.keys(actionsInfo.actions)[0] === "add_schedule") {
                    footerState.dtl = false;
                    footerState.schedule = true;
                } else {
                    footerState.dtl = true;
                    footerState.schedule = false;
                }
            }
        }

        profile['interface']['cards']['today']['footerStateToday'] = footerState;
    }

    // footer state for 7/30 days
    createFooterStateDailyCard(typeChart, days, profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const actions = this.parentalService.getDailyActionsInfo("screentime", "daily", days, profileHash);

        const footer = {
            'display': false,
            'text': '',
            'questions': false,
            'modalParams': {}
        };

        if (([0,1,2,3,4,5,6,7,8].indexOf(actions['message_id']) !== -1)) {
            footer.text = 'parental.screentime.daily.text_' + actions['message_id'];
            footer.questions = !this.utilsCommonService.isEmptyObject(actions['actions']);
            if (footer.questions && actions) {
                footer.display = true;
                const msgId = actions['message_id'];
                if (this.parentalValuesService.messageLinkTextMap[msgId]) {
                    footer.modalParams = {
                        'actions': actions['actions'],
                        'text': this.parentalValuesService.messageLinkTextMap[msgId][0],
                        'link': this.parentalValuesService.messageLinkTextMap[msgId][1]
                    }
                }
            }
        }

        profile['interface']['cards'][typeChart]['footer'] = footer;
    }

    createLegendDailyCard(typeChart, profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);

        const chart = this.usefulService.getNested(profile, {}, 'interface', 'cards', typeChart, 'chart');

        this.translate.get('parental.screentime.late.hours').subscribe( resp => {
            chart['legend'] = {};
            chart['legend']['content'] = resp.replace('{{startHour}}', 
                                                        this.parentalValuesService.lateHoursInterval.start
                                                        .toString())
                                                        .replace('{{endHour}}', 
                                                                this.parentalValuesService.lateHoursInterval.end.toString());
            chart['label'].filter( item => {
                if (item.type) {
                    chart['legend']['display'] = true;
                }
            });
        });

        const text = [];
        if (chart['legend'] && chart['legend'].display) {
            text.push('<ul class="list-legend">');
            text.push('<li><span> \uD83D\uDD34\u000A' + `<span class="text-bullet">${chart['legend'].content}</span>`);
            text.push('</span></li>');
            text.push('</ul>');
        }
        chart['legend']['text'] = text.join("");
    }

    computeLabelChart(typeChart, typeDays, profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const reports = this.parentalService.getDailyReportsArray("screentime", "daily", typeDays, profileHash);
        const label = [];

        for (let i = 0; i < reports.length; i++) {
            label[i] = {};
            label[i]['value'] = (typeDays == 'seven') ? '' : this.parentalHelperService.computeDateFromTimestamp(reports[i].timestamp);

            if (reports[i].late_hours) {
                label[i]['type'] = reports[i].late_hours;
                label[i]['bullet'] = '\uD83D\uDD34\u000A '; // this is the red bullet for late-hours property in unicode
            } else {
                label[i]['type'] = reports[i].late_hours;
            }

            const date = new Date();

            if (typeDays === 'seven') {
                label[i]['key'] = this.valuesService.daysWeek[reports[i]['indexDay']];
                if (reports[i]['indexDay'] === date.getDay()-1 && reports[i]['future_day'] === false) {
                    label[i]['yesterday'] = true
                } else if (reports[i]['indexDay'] === date.getDay()) {
                    label[i].key = 'parental.key.today'
                }
            } else {
                if ((label[i]['value']['day'] === date.getDate() && label[i]['value']['month'] === date.getMonth() + 1)) {
                    label[i]['today'] = true;
                } else if (label[i]['value']['day'] === date.getDate() - 1 && label[i]['value']['month'] === date.getMonth() + 1) {
                    label[i]['yesterday'] = true;
                }
            }
        }

        profile['interface']['cards'][typeChart]['chart']['label'] = label;
    }

    // compute data for 7/30 days chart
    computeDataDailyArray(typeDays, typeChart, profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const days = (typeDays === 'seven') ? 7 : 30;

        const reportsDailyArray = this.parentalService.getDailyReportsArray("screentime", "daily", typeDays, profileHash);
        const maxTimeSpent = this.parentalHelperService.getMaxTimeSpent(reportsDailyArray, 'time_spent');
        const scaleFactor = (maxTimeSpent !== 0) ? 1 : 0;

        const dataMaxVar = Array<number>(days).fill(maxTimeSpent);
        const timeSpentDailyArray = Array<number>(days).fill(0);
        const dtlArray = Array<number>(days).fill(0);
        const emptyScreentimeArray = Array<number>(days).fill(0);

        for (let i = 0; i < reportsDailyArray.length; i++) {
            timeSpentDailyArray[i] = reportsDailyArray[i].time_spent * scaleFactor;
            if (isNaN(timeSpentDailyArray[i])) {
                emptyScreentimeArray[i] = (maxTimeSpent !== 0) ? maxTimeSpent : 50;
                dataMaxVar[i] = 0;
            }

            dtlArray[i] = (reportsDailyArray[i].daily_time_limit !== -1) ? reportsDailyArray[i].daily_time_limit : 0;
            if (dataMaxVar[i] === timeSpentDailyArray[i]) {
                dataMaxVar[i] = 0;
            }
        }

        for (let i = 0; i < days; i++) {
            if (dataMaxVar[i] === 0 && timeSpentDailyArray[i] === 0 && emptyScreentimeArray[i] === 0) {
                dataMaxVar[i] = 50;
            }
        }

        profile['interface']['cards'][typeChart]['chart'] = {
            'data_max_var': dataMaxVar,
            'time_spent_daily_array': timeSpentDailyArray,
            'dtl_array': dtlArray,
            'empty_screentime_array': emptyScreentimeArray,
        };
    }

    computeHourlyDataArray(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const reportsHourlyArray = this.parentalService.getHourlyReportsArray("screentime", "hourly", profileHash);

        const timeSpentArray = Array<number>(24).fill(0);
        const timeSpentColors = Array(24).fill(this.parentalValuesService.chartColors.primary);
        const backgroundSchedule = Array<number>(24).fill(0);
        const backgroundValues = Array<number>(24).fill(60);

        for (let i = 0; i < reportsHourlyArray.length; i++) {
            timeSpentArray[i] = reportsHourlyArray[i].time_spent / 60;
            if (reportsHourlyArray[i]["schedule"] || reportsHourlyArray[i]["schedule_name"]) {
                timeSpentColors[i] = this.parentalValuesService.chartColors.gray;
                backgroundSchedule[i] = 60;
                backgroundValues[i] = 0;
            }
        }
        profile['interface']['cards']['today']['chart'] = {
            'time_spent_array': timeSpentArray,
            'time_spent_colors': timeSpentColors,
            'background_schedule': backgroundSchedule,
            'background_values': backgroundValues
        };
    }

    computeTranslateArrayHours(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const amString = "AM";
        const pmString = "PM";
        const labels = Array<Object>(24);
        let shownValueNow;

        for(let i = 0; i < 24; i++) {
            const date = new Date();
            const hour = date.getHours();

            labels[i] = {};
            labels[i]["value"] = this.localizedDatePipe.transform( date.setHours(i) , 'hour'); 
            if (i === hour) {
                labels[i]["now"] = true;
                shownValueNow = this.translate.instant('parental.key.now');
            }
        }

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['cards']) {
            profile['interface']['cards'] = {};
        }

        if (!profile['interface']['cards']['today']) {
            profile['interface']['cards']['today'] = {};
        }

        if (!profile['interface']['cards']['today']['chart']) {
            profile['interface']['cards']['today']['chart'] = {};
        }

        profile['interface']['cards']['today']['chart']['labels'] = labels;
        profile['interface']['cards']['today']['chart']['shownValueNow'] = shownValueNow;
    }

    // tooltip for 7/30 days chart
    computeTooltipDailyChart(tooltipIndex, data, typeDays, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);
        const report       = this.parentalService.getDailyReportsArray("screentime", "daily", typeDays, profileHash);

        const timeSpent = this.parentalHelperService.computeTime('ceil', report[tooltipIndex]["time_spent"]);
        let timeDtl: any = {};
        let label = '';
        let dtlArray = [];

        if (typeDays === 'seven') {
            // for 7 days chart
            dtlArray = profile['interface']['cards']['week']['chart']['dtl_array'];
            timeDtl = this.parentalHelperService.computeTime('ceil', dtlArray[tooltipIndex]);
            label = this.parentalService.computeTranslateLabelDaily(data.labels[tooltipIndex], 'tooltip');
        } else {
            // for 30 days chart
            timeDtl = this.parentalHelperService.computeTime('ceil', data.datasets[1].data[tooltipIndex]);
            label = this.parentalService.computeTranslateLabelMonthly(data.labels[tooltipIndex]);
        }

        let timeSpentDivided = '';
        let timeDtlDivided = '';

        forkJoin([this.translate.get(['parental.screentime.time_spent', 'parental.screentime.dtl'])])
        .subscribe( resp => {
            timeSpentDivided = resp[0]['parental.screentime.time_spent'].replace('{{hours}}', timeSpent.hours.toString()).replace('{{minutes}}', timeSpent.minutes.toString());
            timeDtlDivided = resp[0]['parental.screentime.dtl'].replace('{{hours}}', timeDtl.hours.toString()).replace('{{minutes}}', timeDtl.minutes.toString());
        });

        if (!isNaN(timeSpent.hours) && !isNaN(timeSpent.minutes)) {
            if ((dtlArray && dtlArray[tooltipIndex] > report[tooltipIndex]["time_spent"]) || (data.datasets[1].data[tooltipIndex] > report[tooltipIndex]["time_spent"])) {
                return label.concat(' ', timeSpentDivided, ' ', timeDtlDivided);
            } else {
                return label.concat(' ', timeSpentDivided);
            }
        } else {
            return label;
        }

    }

    computeTooltipTodayChart(tooltipIndex, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);
        const report       = this.parentalService.getHourlyReportsArray("screentime", "hourly", profileHash);

        if (report[tooltipIndex] && (report[tooltipIndex].schedule || report[tooltipIndex].schedule_name)) {
            return (report[tooltipIndex].schedule_name) ? report[tooltipIndex].schedule_name : "In schedule";
        } else {
            const timeSpent = this.parentalHelperService.computeTime('ceil', report[tooltipIndex]["time_spent"]);
            const labels = profile['interface']['cards']['today']['chart'].labels;
            const index = (tooltipIndex === 23) ? "12AM" : labels[tooltipIndex + 1].value;
            let timeSpentDivided = '';
            this.translate.get("parental.screentime.time_spent")
            .subscribe( resp => {
                timeSpentDivided = resp.replace('{{hours}}', timeSpent.hours.toString()).replace('{{minutes}}', timeSpent.minutes.toString());
            });
            return labels[tooltipIndex].value.concat(" - ", index, '  ', timeSpentDivided);
        }
    }

    createDTLInterface(profileHash) {
        const profiles     = this.parentalService.get();
        const limits   = this.usefulService.getNested(profiles, [], profileHash, "daily_time_limit", "values");
        const noDTL  = -1;
        for (const indexLimit in limits) {
            if (limits.hasOwnProperty(indexLimit)) {
                const days      = this.usefulService.getNested(limits[indexLimit], {}, "days");
                let auxDTL    = 0;
                const textIndex = {};

                for (let indexDay = 0; indexDay < days.length; indexDay++) {
                    if (days[indexDay] > noDTL) {
                        textIndex[indexDay] = true;
                        auxDTL = days[indexDay];
                    }
                }

                limits[indexLimit]["textIndex"] = textIndex;
                limits[indexLimit]["timeDTL"]   = this.parentalHelperService.computeTime("ceil", auxDTL/1000);
            }
        }
    }

    createLimitsInterface(profileHash) {
        const profiles       = this.parentalService.get();
        const profile        = this.usefulService.getNested(profiles, {}, profileHash);
        const limits         = this.usefulService.getNested(profiles, [], profileHash, "daily_time_limit", "values");
        const noDTL        = -1;
        const MAX_INTERVAL = 143;
        const dailyIntervals = [{ "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null },
                              { "index_big_array": MAX_INTERVAL, "id_limit": null }];
        const hashLimits = {};

        for (const indexLimit in limits) {
            if (limits.hasOwnProperty(indexLimit)) {
                const days            = this.usefulService.getNested(limits[indexLimit], {}, "days");
                const newSet          = new Set();
                let indexInBigArray = null;
                let trueIndex = null;

                for (let indexDay = 0; indexDay < days.length; indexDay++) {
                    let newEntry = {
                        "true_index": null,
                        "index_big_array": null,
                        "id_limit": null
                    };

                    if (days[indexDay] > noDTL) {
                        indexInBigArray = Math.floor(days[indexDay]/(3600*1000))*6 + (days[indexDay]%(3600*1000))/(60*1000*10) - 1; // get the index in interval
                        trueIndex = indexInBigArray;
                        indexInBigArray= Math.floor(indexInBigArray);
                        newEntry = {
                            "true_index": trueIndex,
                            "index_big_array": indexInBigArray,
                            "id_limit": limits[indexLimit].restriction_id
                        };
                        newSet.add(indexDay);
                        dailyIntervals[indexDay] = newEntry;
                    }
                }

                limits[indexLimit]["index_big_array"] = indexInBigArray;
                hashLimits[indexInBigArray] = {
                    "id_limit": limits[indexLimit].restriction_id,
                    "set_days": newSet
                };
            }
        }

        profile["limits_interface"]      = dailyIntervals;
        profile["limits_interface_hash"] = hashLimits;
    }

    updateHoursObject(schedule, objectHours) {
        const arraySchedule = schedule.schedule;
        const intervals: any = [];
        const today = new Date().getDay();
        const todayStart = today * 24 * 3600;
        const todayEnd =  (today + 1) * 24 * 3600;
        for (const schedule of arraySchedule) {
            if (schedule[0] >= todayStart && schedule[1] <= todayEnd) {
                intervals.push(schedule);
            } else if (schedule[1] > todayStart && schedule[0] <= todayStart) {
                intervals.push([todayStart, schedule[1]]);
            } else if (schedule[0] < todayEnd && schedule[1] >= todayEnd) {
                intervals.push([schedule[0], todayEnd]);
            }
        }
        const detailedIntervals: any = [];
        for (const interval of intervals) {
            const start = {
                start_h: Math.floor((interval[0] - today * 24 * 3600) / 3600),
                start_m: 60 - Math.floor((interval[0] - today * 24 * 3600) % 3600 / 60)
            }
            let end = {
                end_h: Math.floor((interval[1] - today * 24 * 3600) / 3600),
                end_m: Math.floor((interval[1] - today * 24 * 3600) % 3600 / 60)
            }
            if (end.end_h === 24 && end.end_m === 0) {
                end = {
                    end_h: 23,
                    end_m: 60
                }
            }
            detailedIntervals.push([start, end]);
        }
        
        for (const interval of detailedIntervals) {
            if (interval[0].start_h === interval[1].end_h) {
                objectHours["hours"][interval[0].start_h] += interval[1].end_m - (60 - interval[0].start_m);
                objectHours["schedules"][interval[0].start_h] = schedule.label;
            } else {
                const hoursDifference = interval[1].end_h - interval[0].start_h;

                objectHours["hours"][interval[0].start_h] += interval[0].start_m;
                objectHours["schedules"][interval[0].start_h] = schedule.label;

                objectHours["hours"][interval[1].end_h] += interval[1].end_m;
                objectHours["schedules"][interval[1].end_h] = schedule.label;

                if (hoursDifference > 1) {
                    for (let i = 1; i < hoursDifference; i++) {
                        objectHours["hours"][interval[0].start_h + i] += 60;
                        objectHours["schedules"][interval[0].start_h + i] = schedule.label;
                    }
                }
            }
        }
    }

    createWebCardInterface(type, secondType, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);

        let counter = 0;
        let categories = {};

        while (counter < 3) {
            switch (counter) {
                case 0:
                    categories = this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'categories_allowed', 'categories_allowed_hits', secondType);
                    break;
                case 1:
                    categories = this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'categories_blocked', 'categories_blocked_hits', secondType);
                    break;
                default:
                    categories = this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'websites_accessed', 'websites_accessed_hits', secondType);
            }
            if (categories) {
                for (const categ of categories["categories"]) {
                    categ.amount = (Math.floor(categ.hits / categories["hits"] * 100) < 1) ? "<1" : Math.floor(categ.hits / categories["hits"] * 100);
                    categ.percentage = (categ.amount === "<1") ? 1 : Math.floor(categ.hits / categories["max_hits"] * 100);
                }
            }
            counter++;
        }

        profile['interface']['websites'] = this.usefulService.getNested(profile, {}, 'interface', 'websites');
        profile['interface']['websites'] = {
            "allowed": this.parentalService.getCategories(this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'categories_allowed', 'categories_allowed_hits', secondType)),
            "blocked": this.parentalService.getCategories(this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'categories_blocked', 'categories_blocked_hits', secondType)),
            "websites": this.parentalService.getCategories(this.parentalService.getCategoriesTypeInfo(profileHash, "web", type, 'websites_accessed', 'websites_accessed_hits', secondType))
        };
    }

    createAppInterface(app, deviceName) {
        const newApp = {};
        newApp["description"] = app["description"];
        newApp["time"] = this.parentalHelperService.computeTime("ceil", app["time_spent"], true);
        newApp["time_spent"] = (app["time_spent"] < 60) ? (1 / 2) : Math.floor(app["time_spent"] / 60);
        newApp["device_name"] = deviceName;
        newApp["icon"] = '';
        newApp["message_time"] = this.createTimeOnApp(newApp["time"]);
        return newApp;
    }


    createAppCardInterface(type, secondType, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);
        const apps = this.parentalService.getReportsArray_new(profileHash, type, "apps", secondType);
        const newArrApps = [];
        let maxTimeSpent = 0;

        for(const app of apps) {
            if (app.time_spent > 0) {
                const deviceName = this.usefulService.getNested(profile, null, "devices_monitored", "devices_supported", app.device_id, "display_name");
                if (!deviceName) {
                    continue;
                }

                const newApp = this.createAppInterface(app, deviceName);
                newArrApps.push(newApp);
                if (newApp["time_spent"] > maxTimeSpent) {
                    maxTimeSpent = newApp["time_spent"];
                }
            }
        }
        newArrApps.sort((a,b) => (a.time_spent < b.time_spent) ? 1 : ((b.time_spent < a.time_spent) ? -1 : 0));

        profile['interface'] = this.usefulService.getNested(profile, {}, 'interface');
        profile['interface'][type] = this.usefulService.getNested(profile, {}, 'interface', type);
        profile['interface'][type]['apps'] = newArrApps;
        profile['interface'][type]['apps'].max_time_spent = maxTimeSpent;
    }

    createApplicationsInterface(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const apps = this.usefulService.getNested(profile, {}, "apps", "usage");
        const newArrayUsageApps = [];

        for (const deviceId in apps) {
            const newArrayApps = [];
            let maxTimeSpent = 0;
            const deviceName = this.usefulService.getNested(profile, null, "devices_monitored", "all", deviceId, "display_name");

            // daca device-ul a fost sters/dezasignat nu adaug nimic legat de el in interfata cu aplicatii
            if (!deviceName) {
                continue;
            }

            for (const app of apps[deviceId]) {
                if (app.time_spent > 0) {
                    const newApp = this.createAppInterface(app, deviceName);
                    newArrayApps.push(newApp);
                    if (newApp["time_spent"] > maxTimeSpent) {
                        maxTimeSpent = newApp["time_spent"];
                    }
                }
            }
            newArrayApps.sort((a, b) => (a.time_spent < b.time_spent) ? 1 : -1);

            const auxObj = {
                "max_time_spent": maxTimeSpent,
                "device_name": deviceName,
                "device_id": deviceId,
                "values": newArrayApps
            };
            newArrayUsageApps.push(auxObj);
        }

        newArrayUsageApps.sort((a, b) => (a.device_name.toLowerCase() < b.device_name.toLowerCase()) ? -1 : 1)

        profile['interface'] = this.usefulService.getNested(profile, {}, 'interface');
        profile['interface']['apps'] = this.usefulService.getNested(profile, {}, 'interface', 'apps');
        profile['interface']['apps']['usage'] = newArrayUsageApps;
    }

    createManageApplicationsInterface(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);
        const apps = this.usefulService.getNested(profile, {}, "apps", "installed_apps");
        const newArrayApps = [];

        for (const deviceId in apps) {
            const profileDevices = this.usefulService.getNested(profile, null, "devices_monitored", "all");
            // device dow not belong to the profile anymore
            if (!profileDevices.hasOwnProperty(deviceId)) { 
                continue;
            }

            const deviceName = this.usefulService.getNested(profile, null, "devices_monitored", "all", deviceId, "display_name");
            if (profileDevices[deviceId].device_os === OperatingSystems.IOS) {
                const arrayApps = [];
                let restricted = false;
                for (const app of apps[deviceId]) {
                    arrayApps.push(app.description);
                    if (app.value && app.value === 'all.third.party.apps') {
                        restricted = app.restricted;
                    }
                }
                const auxObj = {
                    "all": arrayApps,
                    "restricted": restricted,
                    "device_id": deviceId,
                    "device_name": deviceName
                }
                newArrayApps.push(auxObj);
            } else {
                const arrayNewApps = [];
                const arrayAllApps = [];
                for (const app of apps[deviceId]) {
                    const newApp = {};
                    newApp['name'] = app.name;
                    newApp['description'] = app.description;
                    newApp['restricted'] = app.restricted;

                    const currentDate = new Date();
                    const reportedDate = new Date(app.reported * 1000);
                    const timeDiff = Math.abs(currentDate.getTime() - reportedDate.getTime());
                    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
                    if (daysDiff <= 1) {
                        arrayNewApps.push(newApp);
                    } else {
                        arrayAllApps.push(newApp);
                    }
                }
                arrayNewApps.sort((a, b) => ((a.restricted === b.restricted) ? 0 : a.restricted ? -1 : 1));
                arrayAllApps.sort((a, b) => ((a.restricted === b.restricted) ? 0 : a.restricted ? -1 : 1));
                const auxObj = {
                    "new": arrayNewApps,
                    "all": arrayAllApps,
                    "unsorted": arrayNewApps.concat(arrayAllApps),
                    "device_id": deviceId,
                    "device_name": deviceName
                }

                newArrayApps.push(auxObj);
            }            
        }
        newArrayApps.sort((a, b) => (a.device_name.toLowerCase() < b.device_name.toLowerCase()) ? -1 : 1)

        profile['interface'] = this.usefulService.getNested(profile, {}, 'interface');
        profile['interface']['apps'] = this.usefulService.getNested(profile, {}, 'interface', 'apps');
        profile['interface']['apps']['installed'] = newArrayApps;
    }

    decideNotSupportedState(profile) {
        let notSupportedState = {
            "ios": false,
            "iot": false,
            "only_iot": false,
            "ios_iot": false
        };
        if (Object.keys(profile.devices_monitored.devices_supported).length === 0) {
            const countIos = Object.keys(profile.devices_monitored.ios).length;
            const countIot = Object.keys(profile.devices_monitored.iot).length;
            const notSupLength = Object.keys(profile.devices_monitored.devices_not_supported).length;
            notSupportedState = {
                "ios": countIos !== 0,
                "iot": countIot !== 0,
                "only_iot": countIos === 0 && countIot > 0 && notSupLength === countIot,
                "ios_iot": countIos !== 0 && countIot !== 0 && notSupLength !== 0,
            };
        }
        return notSupportedState;
    }

    createNotSupportedDevicesInterface(profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);
        const supported    = !this.utilsCommonService.isEmptyObject(this.usefulService.getNested(profile, {}, "devices_monitored", "devices_supported"));
        const ios          = !this.utilsCommonService.isEmptyObject(this.usefulService.getNested(profile, {}, "devices_monitored", "ios"));
        const iot          = !this.utilsCommonService.isEmptyObject(this.usefulService.getNested(profile, {}, "devices_monitored", "iot"));

        let notSupportedInterface = {};
        if (!supported) {
            notSupportedInterface = {
                "ios": ios,
                "iot": iot,
                "only_iot": (ios && !iot),
                "ios_iot": (ios && iot),
                "onlyNotSupported": (ios || iot)
            }
        }

        if(!profile['interface']){
            profile['interface'] = {};
        }
        profile['interface']['devicesNotSupported'] = notSupportedInterface;
    }

     /**
     * changes the time message depending on the hours and minutes
     * if h = 0, return only minutes
     * if h = 0, m = 0, return < 1m
     */
    createTimeOnApp(time) {
        let message = '';
        this.translate.get('parental.screentime.time_spent')
        .subscribe( resp => message = resp.replace('{{hours}}', time.hours.toString()).replace('{{minutes}}', time.minutes.toString()) );

        if (time.hours === 0 && time.minutes === 0 && time.seconds < 60) {
            return '< ' + message.split(/[ ,]+/)[1].replace(time.minutes, '1');
        } else if (time.hours === 0) {
            return message.split(/[ ,]+/)[1];
        } else {
            return message;
        }
    }

    createHoursObject(schedules, profileHash) {
        const objectHours = {
            "hours": Array<number>(24).fill(0),
            "schedules": Array<string>(24).fill('')
        };
        for (const schedule of schedules) {
            if (schedule.active) {
                this.updateHoursObject(schedule, objectHours);
            }
        }
        this.parentalService.setHourlyReportsArray("screentime", "hourly", objectHours, profileHash);
    }

    createSchedulesInterface(profileHash) {
        let helperObject: any = {};
        const profiles = this.parentalService.get();
        const schedules = this.usefulService.getNested(profiles, [], profileHash, "schedules", "values");

        for (const schedule of schedules) {
            schedule.schedule_hash = this.usefulService.crc32Convert(schedule.schedule_id);
            helperObject = this.parentalHelperService.timestampToDailyHours(schedule.schedule);
            for (const key in helperObject) {
                this.usefulService.setNested(schedule, helperObject[key], key);
            }
        }
    }


    countDevices() {
        let count = 0;
        const parentalDevices = this.devicesService.getParentalDevicesList();
        for (const device of parentalDevices) {
            if (device.apps) {
                for(const app of device.apps) {
                    if (app["app_id"] === this.valuesService.bundlePA && app.state === 1) {
                        count++;
                        break;
                    }
                }
            }
        }
        return {
            "all": parentalDevices.length ? parentalDevices.length : '',
            "pa": count
        };
    }

    hasParental(device) {
        if (device.apps && device.apps.length) {
            for(const app of device.apps) {
                if (app['app_id'] === this.valuesService.bundlePA && app.state && app.enable !== 0) {
                    // needs installing: state = 0, enable = 0
                    // pending installation: state = 0, enable = 1
                    // has PA installed: state = 1, enable !== 0
                    return true;
                }
            }
        }
        return false;
    }

    hasParentalNoAgent(device) {
        if (device.apps && device.apps.length) {
            for(const app of device.apps) {
                if (app['app_id'] === this.valuesService.bundlePA && app.state) {
                    return true;
                }
            }
        }
        return false;
    }

    parentalIsEnabled(device) {
        if (device.apps && device.apps.length) {
            for(const app of device.apps) {
                if (app['app_id'] === this.valuesService.bundlePA && app.enable) {
                    return true;
                }
            }
        }
        return false;
    }

    findOriginalIndex(index) {
        const devices = this.devicesService.getParentalDevices();
        for (let i = 0; i < devices.length; i++) {
            if (devices[i].device_id === index) {
                return i;
            }
        }
    }

    canCallDecrement(index) {
        const devices = this.devicesService.getParentalDevices();
        let device  = {};
        if (index >= 0 && index < devices.length) {
            device = devices[index];
        }
        const deviceInterfaceSeconds = this.usefulService.getNested(device, 0, 'interface', 'seconds');
        return (deviceInterfaceSeconds == this.parentalValuesService.maxSecondsCounter);
    }

    canCallGetCoords(profileHash) {
        const profiles               = this.parentalService.get();
        const deviceInterfaceSeconds = this.usefulService.getNested(profiles, 0, profileHash, 'interface', 'location', 'selectedDevice', 'interface', 'seconds');
        return (deviceInterfaceSeconds == 0);
    }

    computeDevicesForLocation(profileHash) {
        const profiles       = this.parentalService.get();
        const profile        = this.usefulService.getNested(profiles, {}, profileHash);
        const mobileDevices  = this.usefulService.getNested(profile, {}, 'devices_monitored', 'mobile');
        const desktopDevices = this.usefulService.getNested(profile, {}, 'devices_monitored', 'desktop');
        const iotDevices     = this.usefulService.getNested(profile, {}, 'devices_monitored', 'iot');

        let toLocate = [];
        let notLocate = [];

        // ordinea device-urilor in dropdown este urmatorea:
        // primele sunt device-uri care suporta localizarea
        // apoi sunt cele care nu suporta localizarea
        // aceste 2 categorii sunt ordonate alfabetic
        // si ordonarea se face per categorie
        toLocate  = Object.values(mobileDevices);
        notLocate = Object.values({...desktopDevices, ...iotDevices});

        for (const device of toLocate) {
            if (!this.hasParental(device)) {
                device['label'] = 'parental.dashboard.location.card.label.need_attention';
            }
        }

        for (const device of notLocate) {
            device['label'] = 'parental.dashboard.location.card.label.not_supported';
        }

        toLocate.sort((a,b) => (a.display_name > b.display_name) ? 1 : -1);
        notLocate.sort((a,b) => (a.display_name > b.display_name) ? 1 : -1);
        const devices = toLocate.concat(notLocate);

        // daca nu avem niciun
        const counterDevices = devices.length;
        if (devices.length === 0) {
            devices.unshift({ placeholder: 'parental.dashboard.location.card.disabled.select' });
        } else if (devices.length > 1) {
            devices.unshift({ placeholder: 'parental.dashboard.location.card.select' });
        }

        return { "devices": devices, "counterDevices": counterDevices };
    }

    createLocationDeviceInterface(profileHash, index, updateDevices?) {
        const profiles          = this.parentalService.get();
        const locationInterface = this.usefulService.getNested(profiles, {}, profileHash, 'interface');
        // daca coordonatele sunt null, inseamna ca nu s-a facut localizarea deloc pana acum pe niciun device
        const allCoords         = this.usefulService.getNested(profiles, null, profileHash, 'interface', 'location', 'allDevicesCoords');
        const list              = this.usefulService.getNested(profiles, [], profileHash, 'interface', 'location', 'list');
        const counter           = this.usefulService.getNested(profiles, 0, profileHash, 'interface', 'location', 'counterDevices');

        const location = {
            selectedDevice      : {},
            selectedDeviceIndex : 0,
            list,
            counterDevices      : counter,
            allDevicesCoords    : allCoords // coordonatele se reseteaza de fiecare data cand se alege un alt device (se fac null ,ele sunt un array)
        };

        if (updateDevices) {
            const devicesObject       = this.computeDevicesForLocation(profileHash);
            location.list           = devicesObject.devices;
            location.counterDevices = devicesObject.counterDevices;
        }

        // selectezi device-ul daca indexul se incadreaza in conditii
        if (index >= 0 && index < location.list.length) {
            location.selectedDevice = location.list[index];
            location.selectedDeviceIndex = index;
        }

        locationInterface["location"] = location;
    }

    setAllCoords(newCoords, profileHash)  {
        const profiles     = this.parentalService.get();
        const location     = this.usefulService.getNested(profiles, {}, profileHash, 'interface', 'location');
        const list         = this.usefulService.getNested(profiles, [], profileHash, 'interface', 'location', 'list');

        if (newCoords) {
            // cand se aduc noi coordonate, se reseteaza flagul de dirty
            for (const location of list) {
                const deviceInterface = location.interface;
                if (deviceInterface && deviceInterface.dirty) {
                    deviceInterface.dirty = false;
                }
            }
        }
        location["allDevicesCoords"] = newCoords;
    }

    createLocationInterfacePerDevice(profileHash, localize?) {
        const profiles                      = this.parentalService.get();
        const deviceSelected                = this.usefulService.getNested(profiles, {}, profileHash, 'interface', 'location', 'selectedDevice');
        const deviceInterfaceSeconds        = this.usefulService.getNested(profiles, 0, profileHash, 'interface', 'location', 'selectedDevice', 'interface', 'seconds');
        const deviceInterfaceDirty          = this.usefulService.getNested(profiles, false, profileHash, 'interface', 'location', 'selectedDevice', 'interface', 'dirty');
        const allCoords                     = this.usefulService.getNested(profiles, null, profileHash, 'interface', 'location', 'allDevicesCoords');

        const deviceInterface = {
            zoom          : 10,
            message       : '',
            isDisabled    : false,
            seconds       : deviceInterfaceSeconds,
            dirty         : deviceInterfaceDirty, // flag care spune daca s-a mai facut localizarea pe un device
            coords        : {}
        };

        // daca nu ai device-uri sau nu ai selctat/preselectat nimic
        if (deviceSelected.placeholder) {
            deviceInterface.isDisabled = true;
        } else {
            // daca device-ul nu suporta localizarea
            if (deviceSelected.label === 'parental.dashboard.location.card.label.not_supported') {
                deviceInterface.dirty = true;
                deviceInterface.isDisabled = true;
            // daca device-ul nu are parental si deci nu putem localiza
            } else if (deviceSelected.label === 'parental.dashboard.location.card.label.need_attention') {
                // mesajul apare doar atunci cand se da pe 'locate'
                deviceInterface.message = localize ? 'parental.dashboard.location.card.error.need_attention' : '';
                deviceInterface.dirty = true;
                deviceInterface.zoom = 10;
                deviceInterface.isDisabled = false;
            // daca putem 100% sa localizam
            } else {
                // cautam si luam coordonatele din obiectul cu coordonate doar daca se apasa pe butonul de 'locate'
                deviceInterface.coords = localize ? this.getDeviceCoords(deviceSelected, profileHash): {};
                // daca se apasa pe 'locate' zoom-ul se face de 16, in rest ramane 10 la alegerea device-ului
                deviceInterface.zoom = !localize ? 10 : 16;

                // daca inca nu au trecut 60 secunde de la ultima localizare
                if (deviceInterface.seconds && deviceInterfaceDirty && localize && !this.utilsCommonService.isEmptyObject(deviceInterface.coords)) {
                    deviceInterface.message = 'parental.dashboard.location.card.error';
                // daca au trecut 60 de secunde sau device-ul este localizat pt pprima oara
                } else if (allCoords !== null) {
                    // daca incerci sa il localizezi
                    if (localize) {
                        deviceInterface.dirty = true;
                        if (!deviceInterface.seconds) {
                            deviceInterface.seconds = this.parentalValuesService.maxSecondsCounter;
                        }
                    }

                    // s-a facut o localizare, dar nu s-a gasit device-ul in lista de device-uri localizate
                    if (this.utilsCommonService.isEmptyObject(deviceInterface.coords) && allCoords !== null && localize) {
                        deviceInterface.message = 'parental.dashboard.location.card.error.no.location';
                    }
                }
            }
        }

        deviceSelected["interface"] = deviceInterface;
    }

    getDeviceCoords(device, profileHash) {
        const deviceId     = this.usefulService.getNested(device, {}, 'device_id');
        const profiles     = this.parentalService.get();
        const allCoords    = this.usefulService.getNested(profiles, [], profileHash, 'interface', 'location', 'allDevicesCoords');
        let coords       = {};

        // daca s-a facut o localizare
        if (allCoords !== null && allCoords[deviceId]) {
            coords = {
                'latitude' : allCoords[deviceId].latitude,
                'longitude': allCoords[deviceId].longitude,
                'image'    : this.parentalValuesService.pinMapLocationCard
            };
        }

        return coords;
    }

    decrementLocateTime(index) {
        const devices = this.devicesService.getParentalDevices();
        let device  = null;
        if (index >= 0 && index < devices.length) {
            device = devices[index];
        }

        if (device) {
            const deviceInterface = this.usefulService.getNested(device, {}, 'interface');
            if (!deviceInterface.placeholder && deviceInterface.seconds) {
                deviceInterface.seconds--;
                if (deviceInterface.seconds <= 0) {
                    deviceInterface.message = '';
                }
                setTimeout(() => { this.decrementLocateTime(index); }, 1000);
            }
        }
    }

    addDeviceNameToContacts(contacts, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);

        if (contacts) {
            for (const contact of contacts) {
                contact["device_name"] = this.usefulService.getNested(profile, '', "devices_monitored", "mobile", contact.device_id, "display_name");
            }
        }
    }

    createContactsInterface(type, secondType, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);

        let onlynotSupported = false;
        let contacts = this.parentalService.getReportsArray_new(profileHash, type, "contacts", secondType);
        this.addDeviceNameToContacts(contacts, profileHash);
        contacts = contacts.filter(item => item["device_name"] !== '');

        if (Object.keys(profile.devices_monitored.mobile).length === 0 && Object.keys(profile.devices_monitored.all).length !== 0) {
            onlynotSupported = true;
        }

        if(!this.usefulService.getNested(profile, false, 'interface')) {
            profile['interface'] = {};
            profile['interface'][type] = {};
        } else if (!this.usefulService.getNested(profile, false, 'interface', type)) {
            profile['interface'][type] = {};
        }

        profile['interface'][type]['contacts'] = {'list': contacts, 'onlyNotSupported': onlynotSupported};

    }

    createContactsPageInterface(type, profileHash) { // latestAdded all
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);

        const contacts = this.parentalService.getReportsArray(profileHash, "contacts", type);
        this.addDeviceNameToContacts(contacts.list, profileHash);
        contacts.list = contacts.list.filter(item => item["device_name"] !== '');

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['contacts']) {
            profile['interface']['contacts'] = {};
        }

        if (type === 'all') {
            profile['interface']['contacts']['stateFeature'] = contacts.stateFeature;
            contacts.list.sort((a, b) => a['name'].toLowerCase().localeCompare(b['name'].toLowerCase()));
        }

        profile['interface']['contacts'][type] = contacts.list;
    }

    searchContactsByName(searchedName, profileHash) {
        const profiles     = this.parentalService.get();
        const profile      = this.usefulService.getNested(profiles, {}, profileHash);

        const allContacts = this.usefulService.getNested(profile, [], 'interface', 'contacts', 'all');

        const found = [];
        for (const contact of  allContacts) {
            if (contact['name'].toLocaleLowerCase().indexOf(searchedName) > -1 || contact['phoneNumbers'][0].indexOf(searchedName) > -1) {
                found.push(contact);
            }
        }
        return found;
    }

    createDataTodayCard(profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, {}, profileHash);
        if (!this.usefulService.getNested(profile, false, "interface")) {
            profile['interface'] = {};
            profile['interface']['cards'] = {};
            profile['interface']['cards']['today'] = {};
        } else if (!this.usefulService.getNested(profile, false, "interface", "cards")) {
            profile['interface']['cards'] = {};
            profile['interface']['cards']['today'] = {};
        } else if (!this.usefulService.getNested(profile, false, "interface", "cards", "today")) {
            profile['interface']['cards']['today'] = {};
        }

        this.computeHourlyDataArray(profileHash);
        this.computeTranslateArrayHours(profileHash)
        this.decideTextColor(profileHash);
        this.decideFooterTodayCard(profileHash);
        const timeInfo = this.parentalService.getHourlyTimeInfo("screentime", "hourly", profileHash);

        profile['interface']['cards']['today']['timeInfo'] = timeInfo;
        profile['interface']['cards']['today']['timeSpent'] = this.parentalHelperService.computeTime("floor", timeInfo.time_spent);
        profile['interface']['cards']['today']['timeAllowed'] = this.parentalHelperService.computeTime("ceil", timeInfo.daily_limit);
        profile['interface']['cards']['today']['requestTime'] = this.parentalService.getRequestTime();
    }

    createDataDailyCard(typeChart, typeDays, profileHash) {
        const profiles = this.parentalService.get();
        const profile = this.usefulService.getNested(profiles, null, profileHash);

        if(!profile['interface']){
            profile['interface'] = {};
        }

        if (!this.usefulService.getNested(profile, false, "interface")) {
            profile['interface'] = {};
            profile['interface']['cards'] = {};
            profile['interface']['cards'][typeChart] = {};
            profile['interface']['cards'][typeChart]["chart"] = {};
        } else if (!this.usefulService.getNested(profile, false, "interface", "cards")) {
            profile['interface']['cards'] = {};
            profile['interface']['cards'][typeChart] = {};
            profile['interface']['cards'][typeChart]["chart"] = {};
        } else if (!this.usefulService.getNested(profile, false, "interface", "cards", typeChart)) {
            profile['interface']['cards'][typeChart] = {};
            profile['interface']['cards'][typeChart]["chart"] = {};
        } else if (!this.usefulService.getNested(profile, false, "interface", "cards", typeChart, "chart")) {
            profile['interface']['cards'][typeChart]["chart"] = {};
        }

        this.computeDataDailyArray(typeDays, typeChart, profileHash);
        this.computeLabelChart(typeChart, typeDays, profileHash);
        this.createLegendDailyCard(typeChart, profileHash);
        this.createFooterStateDailyCard(typeChart, typeDays, profileHash);
    }

    createInterfaceLocationDevices(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);
        const mobileDevices     = this.usefulService.getNested(profile, {}, 'devices_monitored', 'mobile');
        const desktopDevices    = this.usefulService.getNested(profile, {}, 'devices_monitored', 'desktop');
        const iotDevices        = this.usefulService.getNested(profile, {}, 'devices_monitored', 'iot');

        let allMobile = [];
        let notsupported = [];
        const withLocation= [];
        const notFoundLocation = [];

        allMobile = Object.values(mobileDevices);
        notsupported = Object.values({...desktopDevices, ...iotDevices});

        for (const mobile of allMobile) {
            mobile['icon'] = this.parentalValuesService.icons[mobile.device_os];
            const location = this.usefulService.getNested(profile, {}, 'hourly', 'location', mobile.device_id);
            mobile['location'] = location;
            if (this.utilsCommonService.isEmptyObject(mobile.location)) {
                notFoundLocation.push(mobile);
            } else {
                withLocation.push(mobile);
            }
            if (!this.hasParental(mobile)) {
                mobile['label'] = 'parental.dashboard.location.card.label.need_attention';
            }
        }

        for (const device of notsupported) {
            device['icon'] = this.parentalValuesService.icons[device.device_os];
            device['label'] = 'parental.dashboard.location.card.label.not_supported';
        }

        withLocation.sort((a,b) => (a.display_name > b.display_name) ? 1 : -1);
        notFoundLocation.sort((a,b) => (a.display_name > b.display_name) ? 1 : -1);
        notsupported.sort((a,b) => (a.display_name > b.display_name) ? 1 : -1);
        const devices = withLocation.concat(notFoundLocation).concat(notsupported);

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['hourly']) {
            profile['interface']['hourly'] = {};
        }

        if (!profile['interface']['hourly']['location']) {
            profile['interface']['hourly']['location'] = {};
        }

        profile['interface']['hourly']['location']['devices'] = devices;
    }

    createInterfaceLocationAreas(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);

        let areas = [];
        areas = profile['areas']['list'];

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['areas']) {
            profile['interface']['areas'] = {};
        }

        profile['interface']['areas'] = areas;

    }

    createInterfaceLocationSettings(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);

        const state = profile['locationSettings']['stateFeature'];

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['locationSettings']) {
            profile['interface']['locationSettings'] = {};
        }

        profile['interface']['locationSettings']['stateFeature'] = state;
    }

    createInterfaceWebsitesAccessed(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);
        const webProccessedInfo = [];

        const websitesAccesed = this.usefulService.getNested(profile, [], 'websites', 'daily', 'seven', 'websites_accessed');
        
        const websitesAccesedTotal = this.usefulService.getNested(profile, [], 'websites', 'daily', 'seven', 'websites_accessed_hits');

        for (const website of websitesAccesed) {
            webProccessedInfo.push({
                host: website["host"],
                hits: website["hits"],
                percentage: Math.floor((website["hits"] / websitesAccesedTotal)*100)
            });
        }

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['websites']) {
            profile['interface']['websites'] = {};
        }
        
        profile['interface']['websites']['stats'] = webProccessedInfo;
    }

    createInterfaceCategories(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);
        const categoriesAllowedInfo = [];
        const categoriesBlockedInfo = [];

        const categoriesAllowed       = this.usefulService.getNested(profile, [], 'websites', 'daily', 'seven', 'categories_allowed');
        const categoriesAllowedTotal = this.usefulService.getNested(profile, 1, 'websites', 'daily', 'seven', 'categories_allowed_hits');
        const categoriesBlocked       = this.usefulService.getNested(profile, [], 'websites', 'daily', 'seven', 'categories_blocked');
        const categoriesBlockedTotal = this.usefulService.getNested(profile, 1, 'websites', 'daily', 'seven', 'categories_blocked_hits');

        for (const allowed of categoriesAllowed) {
            categoriesAllowedInfo.push({
                name: allowed.category_key,
                hits: allowed.hits,
                percentage: Math.floor((allowed.hits / categoriesAllowedTotal)*100)
            })
        }

        for (const blocked of categoriesBlocked) {
            categoriesBlockedInfo.push({
                name: blocked.category_key,
                hits: blocked.hits,
                percentage: Math.floor((blocked.hits / categoriesBlockedTotal)*100)
            })
        }

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['websites']) {
            profile['interface']['websites'] = {};
        }
        
        profile['interface']['websites']['categoriesAllowed'] = categoriesAllowedInfo;
        profile['interface']['websites']['categoriesBlocked'] = categoriesBlockedInfo;
    }

    createInterfaceExceptions(profileHash) {
        const profiles          = this.parentalService.get();
        const profile           = this.usefulService.getNested(profiles, {}, profileHash);
        const webExceptionsInfo = this.usefulService.getNested(profile, [], 'websites', 'settings', 'list');
        let allowed = 0;
        let blocked = 0;

        for (const exception of webExceptionsInfo) {
            if (!exception.status) {
                allowed = allowed + 1;
            } else {
                blocked = blocked + 1;
            }
        }

        if (!profile['interface']) {
            profile['interface'] = {};
        }

        if (!profile['interface']['websites']) {
            profile['interface']['websites'] = {};
        }
        
        profile['interface']['websites']['settings'] = {
            list: webExceptionsInfo,
            allowed,
            blocked
        };
    }

}
